import _regeneratorRuntime from "@babel/runtime/helpers/regeneratorRuntime";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _Loader;
import { useCallback, useEffect, useState } from 'react';
import { useAmplitude } from '@crehana/analytics';
import { Loader } from '@crehana/compass.ui';
import cn from 'clsx';
import { PlayroomDialog, PlayroomDialogContent, PlayroomDialogFooter, PlayroomDialogHeader } from "../../../PlayroomDialog";
import { TransactionStatusEnum, TransactionTypeEnum } from "../../../shared/types/graphql/globals.gamification";
import { usePlayroomRewardsTranslations, useRedeemReward } from "../../hooks";
import { ExchangeConfirm, ExchangeError, ExchangeSuccessful } from "./components";
import ExchangeInsufficient from "./components/ExchangeInsufficient";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var ExchangeRewardDialog = function ExchangeRewardDialog(_ref) {
  var _ExchangeConfirm, _ExchangeInsufficient, _ExchangeSuccessful, _ExchangeError;
  var open = _ref.open,
    reward = _ref.reward,
    crehanaCoinsAvailable = _ref.crehanaCoinsAvailable,
    organizationSlug = _ref.organizationSlug,
    customCurrencyName = _ref.customCurrencyName,
    onClose = _ref.onClose,
    onConfirmedProp = _ref.onConfirmed,
    onRedeemError = _ref.onRedeemError,
    onRedeemSuccess = _ref.onRedeemSuccess;
  var _usePlayroomRewardsTr = usePlayroomRewardsTranslations(),
    t = _usePlayroomRewardsTr.t;
  var _useState = useState('not_allowed'),
    _useState2 = _slicedToArray(_useState, 2),
    currentView = _useState2[0],
    setCurrentView = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    redeemError = _useState4[0],
    setRedeemError = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    acceptTerms = _useState6[0],
    setAcceptTerms = _useState6[1];
  var _useRedeemReward = useRedeemReward(),
    isRedeemingReward = _useRedeemReward.loading,
    redeemReward = _useRedeemReward.redeemReward;
  var _useAmplitude = useAmplitude(),
    logEvent = _useAmplitude.logEvent;
  var shouldRenderFooter = currentView === 'initial' || currentView === 'not_allowed';
  var shouldDisableToExitOnDialog = isRedeemingReward;
  var isCTARedeemDisabled = !acceptTerms || isRedeemingReward || !reward.can_redeemed_this_reward;
  var hasInsufficientCoins = reward.coins > crehanaCoinsAvailable;

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    // Validate if user has insufficient coins to redeem reward
    if (hasInsufficientCoins) {
      setCurrentView('not_allowed');
    } else {
      setCurrentView('initial');
    }
    // Reset all states terms and error when re-open dialog
    setAcceptTerms(false);
    setRedeemError(undefined);
  }, [open, hasInsufficientCoins]);
  var onAcceptTerms = useCallback(function () {
    return setAcceptTerms(function (prev) {
      return !prev;
    });
  }, []);
  var onRequestReward = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    var redeemObject;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _context.prev = 0;
          if (!(acceptTerms && reward)) {
            _context.next = 7;
            break;
          }
          redeemObject = {
            reward_id: reward.originalId,
            organization_slug: organizationSlug,
            quantity: -1,
            transaction_status_enum: TransactionStatusEnum.STATUS_PENDING,
            transaction_type_enum: TransactionTypeEnum.TYPE_EXCHANGED
          };
          _context.next = 5;
          return redeemReward(redeemObject);
        case 5:
          setCurrentView('success');
          logEvent({
            event: 'Game Reward Requested',
            data: {
              'Reward ID': redeemObject.reward_id,
              'Organization Slug': redeemObject.organization_slug,
              Quantity: redeemObject.quantity,
              'Transaction Status': redeemObject.transaction_status_enum,
              'Transaction Type': redeemObject.transaction_type_enum
            }
          });
        case 7:
          _context.next = 14;
          break;
        case 9:
          _context.prev = 9;
          _context.t0 = _context["catch"](0);
          setCurrentView('error');
          setRedeemError(_context.t0);
          onRedeemError === null || onRedeemError === void 0 ? void 0 : onRedeemError(_context.t0);
        case 14:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[0, 9]]);
  })), [reward, logEvent, acceptTerms, redeemReward, onRedeemError, organizationSlug]);
  var onConfirmed = useCallback(function () {
    onConfirmedProp === null || onConfirmedProp === void 0 ? void 0 : onConfirmedProp();
    onRedeemSuccess === null || onRedeemSuccess === void 0 ? void 0 : onRedeemSuccess(reward);
  }, [onConfirmedProp, onRedeemSuccess, reward]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  var renderModalContent = useCallback(function () {
    if (reward) {
      switch (currentView) {
        case 'initial':
          return _ExchangeConfirm || (_ExchangeConfirm = /*#__PURE__*/_jsx(ExchangeConfirm, {
            reward: reward,
            customCurrencyName: customCurrencyName,
            totalUserCrehanaCoins: crehanaCoinsAvailable,
            onAcceptTerms: onAcceptTerms
          }));
        case 'not_allowed':
          return _ExchangeInsufficient || (_ExchangeInsufficient = /*#__PURE__*/_jsx(ExchangeInsufficient, {
            reward: reward,
            customCurrencyName: customCurrencyName,
            totalUserCrehanaCoins: crehanaCoinsAvailable
          }));
        case 'success':
          return _ExchangeSuccessful || (_ExchangeSuccessful = /*#__PURE__*/_jsx(ExchangeSuccessful, {
            reward: reward,
            onConfirmed: onConfirmed
          }));
        case 'error':
          return _ExchangeError || (_ExchangeError = /*#__PURE__*/_jsx(ExchangeError, {
            onClose: onClose
          }));
      }
    }
  }, [crehanaCoinsAvailable, customCurrencyName, onAcceptTerms, currentView, onConfirmed, redeemError, onClose, reward]);
  return /*#__PURE__*/_jsxs(PlayroomDialog, {
    open: open,
    onClose: onClose,
    disableBackdropClick: shouldDisableToExitOnDialog,
    disableEscapeKeyDown: shouldDisableToExitOnDialog,
    children: [/*#__PURE__*/_jsx(PlayroomDialogHeader, {
      title: t('PRS_CTA_REWARD_TITLE'),
      type: currentView === 'initial' || currentView === 'not_allowed' ? 'default' : 'simple',
      hideClose: shouldDisableToExitOnDialog,
      onClose: onClose
    }), /*#__PURE__*/_jsx(PlayroomDialogContent, {
      children: renderModalContent()
    }), shouldRenderFooter && /*#__PURE__*/_jsx(PlayroomDialogFooter, {
      children: /*#__PURE__*/_jsxs("div", {
        className: "tw-flex tw-justify-end tw-items-center",
        children: [/*#__PURE__*/_jsx("button", {
          type: "button",
          className: cn('tw-btn-tertiary-xs tw-mr-0.8', isRedeemingReward && 'tw-btn-disabled'),
          disabled: isRedeemingReward,
          onClick: onClose,
          children: t('PRS_CTA_REWARD_CANCEL')
        }), /*#__PURE__*/_jsxs("button", {
          type: "button",
          className: cn('tw-btn-primary-xs', isCTARedeemDisabled && 'tw-btn-disabled'),
          disabled: isCTARedeemDisabled,
          onClick: onRequestReward,
          children: [isRedeemingReward && (_Loader || (_Loader = /*#__PURE__*/_jsx(Loader, {
            size: "xs",
            itemClass: "tw-bg-current"
          }))), t(isRedeemingReward ? 'PRS_SECTION_MARKETPLACE_REDEEMING' : 'PRS_CTA_REWARD_SOLICIT')]
        })]
      })
    })]
  });
};
export default ExchangeRewardDialog;